/**
 * Universities domain — React Query hooks.
 *
 * Post-FIR-6997 the universities table reads/mutates through the unified
 * opportunities engine (`services/opportunities`, `common_table_actions`),
 * so this domain's list/summary hooks and wrapped bulk mutations were
 * removed as dead code. What remains is the detail hook the university
 * detail pages consume.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import client from '../apiClient'

import type {
  GetUniversityQueryResponse,
  GetUniversity404,
} from './legacyDetailTypes'

export type {
  GetUniversityQueryResponse,
  GetUniversityPathParams,
} from './legacyDetailTypes'

/**
 * Get university detail.
 *
 * Hand-written wrapper around the generated `getUniversity` client because
 * the legacy hook skips retries on 404 — without that predicate, React
 * Query retries 3× before surrendering, which delays "not found" feedback
 * in the UI. The wrapper accepts an optional id (so callers can pass
 * `undefined` before route resolution) and disables the query until the
 * id arrives.
 */
export function useGetUniversity(
  universityId?: number | string,
  options: {
    query?: Omit<
      UseQueryOptions<GetUniversityQueryResponse, AxiosError<GetUniversity404>>,
      'queryKey' | 'queryFn' | 'enabled' | 'retry'
    >
  } = {}
) {
  const { query = {} } = options
  // The backend route is `<str:university_id>`; callers pass the numeric
  // ContactCompany id, so coerce to the string the generated client expects.
  const id = universityId != null ? String(universityId) : undefined
  return useQuery<GetUniversityQueryResponse, AxiosError<GetUniversity404>>({
    queryKey:
      id !== undefined ? ['university', id] : ['university', universityId],
    // FIR-7878: unified chain detail endpoint (byte-identical response).
    queryFn: async ({ signal }) =>
      (
        await client<GetUniversityQueryResponse>({
          method: 'GET',
          url: `/api/v1/opportunities/detail/${id}/`,
          params: { target_type: 'university' },
          signal,
        })
      ).data,
    enabled: id !== undefined,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error?.response?.status === 404
        ? false
        : failureCount < 3,
    ...query,
  })
}
